import React from 'react';
import { lessonAuthoringError } from '../api/VerbaScholaApi';
import { LessonReviewReadOnlyContext } from './lessonReviewReadOnly';

export interface ExerciseSaveAction {
    valid: boolean;
    /** False for a form to add something that holds nothing (typed and erased): nothing to save, so its edit is cleared. */
    pending: boolean;
    save: () => Promise<unknown>;
    /** The editor keeps its draft in the page's draft store (builderDrafts.tsx): once it closes, its edit is still saved
     * with this action, which keeps its last values, and showing another activity loses nothing. */
    kept: boolean;
}
type Register = (scope: string, action: ExerciseSaveAction) => () => void;
export const ExerciseSaveContext = React.createContext<Register | null>(null);

/** Expose the same validated write as the editor's own Save button; a form to add something passes ``pending``: whether
 * anything is typed in it. ``kept``: the editor's fields are all kept in the page's draft store under ``scope``
 * (useDraftState), so its save stays registered after it closes. Its save must then read the lesson revision when sent
 * (useConfirmedRevision, or the page's handlers), not the one it drew with. */
export function useExerciseSave(scope: string, valid: boolean, save: () => Promise<unknown>, pending = true, kept = false) {
    const register = React.useContext(ExerciseSaveContext);
    const readOnly = React.useContext(LessonReviewReadOnlyContext);
    const latest = React.useRef({ valid, save, pending });
    latest.current = { valid, save, pending };
    React.useLayoutEffect(() => {
        if (!register || readOnly) return;
        return register(scope, { get valid() { return latest.current.valid; }, get pending() { return latest.current.pending; }, save: () => latest.current.save(), kept });
    }, [register, scope, readOnly, kept]);
}

/** ``clear`` takes an edit off the page's unsaved edits (an add form emptied again holds nothing to save). ``saveAll``
 * resolves whether every edit was saved. */
export function useExerciseSaveAll(scopes: React.MutableRefObject<Set<string>>, clear: (scope: string) => void) {
    const actions = React.useRef(new Map<string, ExerciseSaveAction>());
    const running = React.useRef(false);
    const [saving, setSaving] = React.useState(false);
    const [message, setMessage] = React.useState<string | null>(null);
    const [failed, setFailed] = React.useState(false);
    // The edits changed outside a Save all (an editor saved or cancelled, a refresh): its status no longer applies.
    const clearStatus = React.useCallback(() => {
        if (running.current) return;
        setMessage(null); setFailed(false);
    }, []);
    // Save all cannot run now (the page says why): its status line says so, as for a refusal of its own.
    const refuse = React.useCallback((reason: string) => {
        if (running.current) return;
        setFailed(true); setMessage(reason);
    }, []);
    const register = React.useCallback<Register>((scope, action) => {
        actions.current.set(scope, action);
        // A kept editor's save outlives it, until another editor of the scope registers.
        return () => { if (!action.kept && actions.current.get(scope) === action) actions.current.delete(scope); };
    }, []);
    /** Drops the save a closed editor left (its item was deleted); an editor still open keeps its own. */
    const forget = React.useCallback((scope: string) => {
        if (actions.current.get(scope)?.kept) actions.current.delete(scope);
    }, []);
    /** Whether an edit's editor keeps its draft once closed: one that does not would lose it to another view. */
    const kept = React.useCallback((scope: string) => Boolean(actions.current.get(scope)?.kept), []);
    const saveAll = async (): Promise<boolean> => {
        if (running.current) return false;
        // A form to add something that holds nothing is no edit: cleared, not saved, and it blocks nothing.
        for (const scope of [...scopes.current]) if (actions.current.get(scope)?.pending === false) clear(scope);
        const pending = [...scopes.current];
        // Validate every editor before writing any of them. Never silently drop an unhandled scope.
        if (pending.some(scope => !actions.current.get(scope)?.valid)) {
            setFailed(true); setMessage('Complete the required fields in your open editors before saving all.'); return false;
        }
        running.current = true; setSaving(true); setFailed(false); setMessage(null);
        let completed = 0;
        try {
            for (const scope of pending) {
                const action = actions.current.get(scope);
                if (!action?.valid) throw new Error('editor_not_available');
                await action.save();
                // A failed normal save restores its dirty scope; never report that scope as saved.
                if (scopes.current.has(scope)) throw new Error('editor_not_saved');
                completed++;
            }
            setMessage('All exercise changes saved.');
            return true;
        } catch (caught) {
            setFailed(true);
            setMessage(`${completed ? `${completed} ${completed === 1 ? 'item was' : 'items were'} saved. ` : ''}The remaining edits could not be confirmed as saved. ${lessonAuthoringError(caught, 'Your local edits are preserved. Please retry when the connection is available.')}`);
            return false;
        } finally { running.current = false; setSaving(false); }
    };
    return { register, running, saving, message, failed, saveAll, clearStatus, refuse, kept, forget };
}
